import LumaResourceWorkspace, {lumaMoney} from '../../../components/admin/LumaResourceWorkspace';
import AdminTable from '../../../components/admin/AdminTable';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Edit, Trash2, Plus, Search, FileCheck, CheckCircle2, AlertCircle, Eye, Users } from 'lucide-react';
import { Cliente, fetchClientes } from '../../../services/clienteService';
import AddClienteModal from '../../../components/Clients/AddClienteModal';
import EditClienteModal from '../../../components/Clients/EditClienteModal';
import DeleteClienteModal from '../../../components/Clients/DeleteClienteModal';
import ModalVerDeclaracionJurada from '../../../components/Clients/ModalVerDeclaracionJurada';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission';

const ClientsPage: React.FC = () => {
    const [clientes, setClientes] = useState<Cliente[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [clienteToEdit, setClienteToEdit] = useState<Cliente | null>(null);
    const [clienteToDelete, setClienteToDelete] = useState<Cliente | null>(null);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [declaracionModalOpen, setDeclaracionModalOpen] = useState(false);
    const [clienteDeclaracion, setClienteDeclaracion] = useState<{
        nombre?: string;
        email?: string;
        telefono?: string | null;
        ci_nit?: string | null;
        tipo_cliente?: string;
        fecha_declaracion_jurada?: string | null;
        foto_declaracion_jurada_url?: string | null;
    } | null>(null);

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();

    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('cliente', userLevelId);

    const loadClientes = useCallback(async () => {
        if (!perm?.can_read) {
            setClientes([]);
            return;
        }
        try {
            const data = await fetchClientes();
            const sortedData = data.sort((a, b) => a.id - b.id);
            setClientes(sortedData);
        } catch (err) {
            /* Browser diagnostics omitted. */
            setClientes([]);
        }
    }, [perm?.can_read]);

    useEffect(() => {
        loadClientes();
    }, [loadClientes]);

    const openEditModal = (cliente: Cliente) => {
        setClienteToEdit(cliente);
        setIsEditOpen(true);
    };

    const handleUpdateCliente = (updated: Cliente) => {
        setClientes(prev => {
            const updatedList = prev.map(c => (c.id === updated.id ? updated : c));
            return updatedList.sort((a, b) => a.id - b.id);
        });
    };

    const handleAddCliente = (nuevoCliente: Cliente) => {
        setClientes(prev => {
            const updated = [...prev, nuevoCliente];
            return updated.sort((a, b) => a.id - b.id);
        });
    };

    const openDeleteModal = (cliente: Cliente) => {
        setClienteToDelete(cliente);
        setIsDeleteOpen(true);
    };

    const handleClienteDeleted = (deletedId: number) => {
        setClientes(prev => {
            const filteredList = prev.filter(c => c.id !== deletedId);
            return filteredList.sort((a, b) => a.id - b.id);
        });
    };

    const handleVerDeclaracion = (cliente: Cliente) => {
        const nombre = cliente.usuario
            ? `${cliente.usuario.first_name || ''} ${cliente.usuario.last_name || ''}`.trim() || cliente.usuario.email
            : 'Cliente sin nombre';
        
        setClienteDeclaracion({
            nombre,
            email: cliente.usuario?.email,
            telefono: cliente.telefono,
            ci_nit: cliente.ci_nit,
            tipo_cliente: cliente.usuario?.tipo_nivel_usuario?.nombre || 'Cliente',
            fecha_declaracion_jurada: cliente.fecha_declaracion_jurada,
            foto_declaracion_jurada_url: cliente.foto_declaracion_jurada_url,
        });
        setDeclaracionModalOpen(true);
    };

    const filteredClientes = useMemo(() => {
        if (!searchTerm.trim()) return clientes;
        const q = searchTerm.toLowerCase();
        return clientes.filter(c => {
            const name = `${c.usuario?.first_name || ''} ${c.usuario?.last_name || ''}`.toLowerCase();
            const email = (c.usuario?.email || '').toLowerCase();
            const phone = (c.telefono || '').toLowerCase();
            const ci = (c.ci_nit || '').toLowerCase();
            const tipo = (c.usuario?.tipo_nivel_usuario?.nombre || '').toLowerCase();
            return name.includes(q) || email.includes(q) || phone.includes(q) || ci.includes(q) || tipo.includes(q);
        });
    }, [clientes, searchTerm]);

    const totalPages = Math.ceil(filteredClientes.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedClientes = filteredClientes.slice(startIndex, startIndex + itemsPerPage);

    if (perm === null) return <p className="p-8 text-center text-slate-500 font-medium">Cargando permisos…</p>;

    return <><LumaResourceWorkspace title="Clientes" description="Consulta y gestiona la información de tu coworking." group="Usuarios" rows={clientes.map(r=>({...r,nombre_completo:`${r.usuario?.first_name||''} ${r.usuario?.last_name||''}`}))} columns={[{key:'usuario',label:'Nombre',render:r=>`${r.usuario?.first_name||''} ${r.usuario?.last_name||''}`},{key:'email',label:'Email',render:r=>r.usuario?.email||'—'},{key:'telefono',label:'Teléfono'},{key:'ci_nit',label:'CI / NIT'},{key:'estado',label:'Estado',render:r=>r.usuario?.estado===undefined?'—':r.usuario.estado?'Activo':'Inactivo'}]} idKey="id" nameKey="nombre_completo" statusKey="estado" canRead={perm?.can_read} onCreate={perm?.can_create?()=>{setIsAddOpen(true)}:undefined} onEdit={perm?.can_update?r=>{setClienteToEdit(r);setIsEditOpen(true);}:undefined} onDelete={perm?.can_delete?r=>{setClienteToDelete(r);setIsDeleteOpen(true);}:undefined} extraActions={r=><button type="button" onClick={()=>handleVerDeclaracion(r)}>Declaración jurada</button>} history=""/><AddClienteModal
                    isOpen={isAddOpen}
                    onClose={() => setIsAddOpen(false)}
                    onAddCliente={handleAddCliente}
                /><EditClienteModal
                    isOpen={isEditOpen}
                    onClose={() => setIsEditOpen(false)}
                    cliente={clienteToEdit}
                    onUpdate={handleUpdateCliente}
                /><DeleteClienteModal
                    isOpen={isDeleteOpen}
                    onClose={() => setIsDeleteOpen(false)}
                    cliente={clienteToDelete}
                    onDeleted={handleClienteDeleted}
                /><ModalVerDeclaracionJurada
                    isOpen={declaracionModalOpen}
                    onClose={() => setDeclaracionModalOpen(false)}
                    cliente={clienteDeclaracion}
                /><ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                /></>;
};

export default ClientsPage;
